// Mock data for the Academic Scores → Numbers tab.
// Two class bands: "6-10" (5 core subjects) and "11-12" (Higher Secondary,
// 15 subjects + streams). Shapes are chosen to feed the shared components
// (StatTile, MetricCard, AppChart, ReportDataTable) directly.

export type ClassBand = '6-10' | '11-12';

export interface Kpis {
  averageMark: number;
  passPercentage: number;
  compliance: number;
}

export interface MetricSummary {
  studentsExpected: number;
  marksEntered: number;
  strongestSubject: { name: string; value: number };
  weakestSubject: { name: string; value: number };
  below35Pct: number;
}

export interface SubjectProfile {
  labels: string[];
  /** State-average reference polygon. */
  state: number[];
  /** Current selection polygon (band-level here). */
  selected: number[];
}

export interface MarkBand {
  /** X-axis subject labels. */
  subjects: string[];
  /** Series values in lakhs (matches the "L" y-axis in the mock). */
  below35: number[];
  band35_60: number[];
  band61_80: number[];
  above80: number[];
  /**
   * ORIGINAL (unscaled) per-subject band counts. The arrays above are scaled
   * to lakhs for a readable Y axis; these keep the real student counts so the
   * chart's Table View can show them instead of the scaled values.
   */
  below35Raw?: number[];
  band35_60Raw?: number[];
  band61_80Raw?: number[];
  above80Raw?: number[];
  /** Per-subject pass % and average mark (0–100), for the subject-wise chart. */
  passPct: number[];
  avgMark: number[];
}

export interface ExamProgression {
  periods: string[];
  avgMark: number[];
  passPct: number[];
  compliance: number[];
}

export interface ClassWise {
  classes: string[];
  avgMark: number[];
  passPct: number[];
  /** Classes rendered in the "board exam" (maroon) highlight. */
  boardClasses: string[];
}

export interface StreamPerf {
  streams: string[];
  avgMark: number[];
  passPct: number[];
}

export interface SubjectDetailRow {
  subject: string;
  averageMark: number;
  passPct: string;
  below35: number;
  band3560: number;
  band6180: number;
  above80: number;
  absentees: number;
  [key: string]: unknown;
}

export interface DistrictSummaryRow {
  name: string;
  avgMark: number;
  passPct: string;
  compliance: string;
  weakestSubject: string;
  [key: string]: unknown;
}

export interface AcademicData {
  scope: string;
  /** Total number of subjects in this band (full list, not the radar subset). */
  subjectCount?: number;
  subjectCountLabel: string;
  profileBadge: string;
  markBandTitle: string;
  subjectDetailTitle: string;
  kpis: Kpis;
  metrics: MetricSummary;
  subjectProfile: SubjectProfile;
  markBand: MarkBand;
  /** False when markBand holds raw student counts (small scopes), not lakhs. */
  markBandInLakh?: boolean;
  examProgression: ExamProgression;
  classWise: ClassWise;
  /** Only present for the 11-12 band. */
  streamPerf?: StreamPerf;
  subjectDetail: SubjectDetailRow[];
  /** Original unscaled subject detail data for table display */
  originalSubjectDetail?: SubjectDetailRow[];
}

// ─────────────────────────────────────────────
// Shared KPI / metric numbers (same across bands in the mock)
// ─────────────────────────────────────────────

const KPIS: Kpis = {
  averageMark: 65.8,
  passPercentage: 84.4,
  compliance: 86.4,
};

const EXAM_PROGRESSION: ExamProgression = {
  periods: ['Quarterly', 'Half Yearly', 'Annual'],
  avgMark: [61.6, 64.1, 67.9],
  passPct: [82, 84, 87],
  compliance: [84, 85, 86.4],
};

const CLASS_WISE: ClassWise = {
  classes: ['6', '7', '8', '9', '10', '11', '12'],
  avgMark: [63, 63, 62, 63, 67, 64, 68],
  passPct: [85, 85, 84, 85, 90, 86, 91],
  boardClasses: ['10', '12'],
};

// Districts — Academic Summary (shared; 12 of the 38 shown, "38 total").
const DISTRICT_SUMMARY: DistrictSummaryRow[] = [
  {
    name: 'Tiruvallur',
    avgMark: 63.2,
    passPct: '82.5%',
    compliance: '83.8%',
    weakestSubject: 'Maths (55.6)',
  },
  {
    name: 'Chennai',
    avgMark: 66.2,
    passPct: '84.9%',
    compliance: '88.8%',
    weakestSubject: 'Maths (58.7)',
  },
  {
    name: 'Kancheepuram',
    avgMark: 60.2,
    passPct: '80.4%',
    compliance: '92.4%',
    weakestSubject: 'Maths (52.6)',
  },
  {
    name: 'Chengalpattu',
    avgMark: 66.9,
    passPct: '85.4%',
    compliance: '87.1%',
    weakestSubject: 'Maths (59.3)',
  },
  {
    name: 'Ranipet',
    avgMark: 73.8,
    passPct: '90.5%',
    compliance: '82.4%',
    weakestSubject: 'Maths (66.4)',
  },
  {
    name: 'Vellore',
    avgMark: 77.8,
    passPct: '93.3%',
    compliance: '82.3%',
    weakestSubject: 'Maths (70.4)',
  },
  {
    name: 'Tirupathur',
    avgMark: 60.6,
    passPct: '80.7%',
    compliance: '83.1%',
    weakestSubject: 'Maths (53.2)',
  },
  {
    name: 'Dharmapuri',
    avgMark: 70.5,
    passPct: '88.1%',
    compliance: '90.2%',
    weakestSubject: 'Maths (63.2)',
  },
  {
    name: 'Tiruvannamalai',
    avgMark: 70,
    passPct: '87.7%',
    compliance: '94.2%',
    weakestSubject: 'Maths (62.6)',
  },
  {
    name: 'Villupuram',
    avgMark: 63.5,
    passPct: '82.8%',
    compliance: '76.7%',
    weakestSubject: 'Maths (55.9)',
  },
  {
    name: 'Kallakurichi',
    avgMark: 58.7,
    passPct: '79.1%',
    compliance: '90.4%',
    weakestSubject: 'Maths (51.2)',
  },
  {
    name: 'Salem',
    avgMark: 68.6,
    passPct: '86.6%',
    compliance: '78.1%',
    weakestSubject: 'Maths (61.1)',
  },
];

// ─────────────────────────────────────────────
// Band: 6-10 (5 core subjects)
// ─────────────────────────────────────────────

const CORE_SUBJECT_DETAIL: SubjectDetailRow[] = [
  {
    subject: 'Language',
    averageMark: 70.7,
    passPct: '88.8%',
    below35: 425009,
    band3560: 1284522,
    band6180: 1386244,
    above80: 696942,
    absentees: 44577,
  },
  {
    subject: 'Social',
    averageMark: 66.2,
    passPct: '85.5%',
    below35: 551064,
    band3560: 1296462,
    band6180: 1312728,
    above80: 632673,
    absentees: 44379,
  },
  {
    subject: 'English',
    averageMark: 65.7,
    passPct: '85.1%',
    below35: 565350,
    band3560: 1297976,
    band6180: 1304295,
    above80: 625313,
    absentees: 44375,
  },
  {
    subject: 'Science',
    averageMark: 61.7,
    passPct: '82.1%',
    below35: 679846,
    band3560: 1309349,
    band6180: 1235692,
    above80: 568138,
    absentees: 44293,
  },
  {
    subject: 'Maths',
    averageMark: 58.2,
    passPct: '79.4%',
    below35: 782693,
    band3560: 1319605,
    band6180: 1173975,
    above80: 516744,
    absentees: 44322,
  },
];

const CORE_SUBJECTS = CORE_SUBJECT_DETAIL.map((r) => r.subject);
const toLakh = (n: number) => Number((n / 100000).toFixed(2));

const BAND_6_10: AcademicData = {
  scope:
    'Government, Partially Aided & Fully Aided schools only. Class 10 & 12 = board exam (Annual only); classes 6-10 at 5 core subjects, 11-12 are stream-based.',
  subjectCountLabel: 'Class 6-10 (5 subjects)',
  profileBadge: 'Core Subjects (Class 6-10) · 5 subjects',
  markBandTitle: 'Mark Band Distribution — Core Subjects (Class 6-10)',
  subjectDetailTitle: 'Subject Detail — Core Subjects (Class 6-10)',
  kpis: KPIS,
  metrics: {
    studentsExpected: 5253435,
    marksEntered: 4537990,
    strongestSubject: { name: 'Language', value: 70.7 },
    weakestSubject: { name: 'Maths', value: 58.2 },
    below35Pct: 15.8,
  },
  subjectProfile: {
    labels: CORE_SUBJECTS,
    state: [70.7, 66.2, 65.7, 61.7, 58.2],
    selected: [68.4, 64.9, 63.8, 60.1, 56.7],
  },
  markBand: {
    subjects: CORE_SUBJECTS,
    below35: CORE_SUBJECT_DETAIL.map((r) => toLakh(r.below35)),
    band35_60: CORE_SUBJECT_DETAIL.map((r) => toLakh(r.band3560)),
    band61_80: CORE_SUBJECT_DETAIL.map((r) => toLakh(r.band6180)),
    above80: CORE_SUBJECT_DETAIL.map((r) => toLakh(r.above80)),
    // Pass % / Average Mark are 0–100 values, NOT lakh counts — read them
    // straight from the subject detail (passPct is a "88.8%" string).
    passPct: CORE_SUBJECT_DETAIL.map((r) => parseFloat(r.passPct) || 0),
    avgMark: CORE_SUBJECT_DETAIL.map((r) => r.averageMark),
  },
  examProgression: EXAM_PROGRESSION,
  classWise: CLASS_WISE,
  subjectDetail: CORE_SUBJECT_DETAIL,
  originalSubjectDetail: CORE_SUBJECT_DETAIL,
};

// ─────────────────────────────────────────────
// Band: 11-12 (Higher Secondary, 15 subjects + streams)
// ─────────────────────────────────────────────

const HS_SUBJECT_DETAIL: SubjectDetailRow[] = [
  {
    subject: 'History',
    averageMark: 70.1,
    passPct: '88.3%',
    below35: 19969,
    band3560: 57942,
    band6180: 62073,
    above80: 31044,
    absentees: 2079,
  },
  {
    subject: 'Computer Science',
    averageMark: 69.2,
    passPct: '87.7%',
    below35: 21236,
    band3560: 58761,
    band6180: 62134,
    above80: 30994,
    absentees: 2112,
  },
  {
    subject: 'Commerce',
    averageMark: 68.8,
    passPct: '87.4%',
    below35: 22400,
    band3560: 60236,
    band6180: 63345,
    above80: 31394,
    absentees: 2086,
  },
  {
    subject: 'Geography',
    averageMark: 68.6,
    passPct: '87.2%',
    below35: 21889,
    band3560: 58169,
    band6180: 60943,
    above80: 30032,
    absentees: 2074,
  },
  {
    subject: 'Political Science',
    averageMark: 68.1,
    passPct: '87.0%',
    below35: 20450,
    band3560: 57310,
    band6180: 60120,
    above80: 29880,
    absentees: 1998,
  },
  {
    subject: 'Economics',
    averageMark: 67.9,
    passPct: '86.7%',
    below35: 46194,
    band3560: 118534,
    band6180: 123186,
    above80: 60449,
    absentees: 4205,
  },
  {
    subject: 'Zoology',
    averageMark: 67.2,
    passPct: '86.2%',
    below35: 23776,
    band3560: 58658,
    band6180: 60272,
    above80: 29351,
    absentees: 1973,
  },
  {
    subject: 'English',
    averageMark: 66.9,
    passPct: '86.0%',
    below35: 97344,
    band3560: 236529,
    band6180: 242028,
    above80: 117877,
    absentees: 8057,
  },
  {
    subject: 'Botany',
    averageMark: 66.6,
    passPct: '85.8%',
    below35: 24454,
    band3560: 58712,
    band6180: 59833,
    above80: 29026,
    absentees: 2005,
  },
  {
    subject: 'Language',
    averageMark: 66.1,
    passPct: '85.4%',
    below35: 88120,
    band3560: 224510,
    band6180: 231040,
    above80: 112300,
    absentees: 7640,
  },
  {
    subject: 'Accountancy',
    averageMark: 63.3,
    passPct: '83.2%',
    below35: 29750,
    band3560: 60980,
    band6180: 58994,
    above80: 27718,
    absentees: 2019,
  },
  {
    subject: 'Chemistry',
    averageMark: 62.3,
    passPct: '82.5%',
    below35: 60465,
    band3560: 118903,
    band6180: 113302,
    above80: 52609,
    absentees: 3988,
  },
  {
    subject: 'Business Maths',
    averageMark: 61.4,
    passPct: '81.8%',
    below35: 32306,
    band3560: 61197,
    band6180: 57481,
    above80: 26439,
    absentees: 2038,
  },
  {
    subject: 'Physics',
    averageMark: 59.9,
    passPct: '80.6%',
    below35: 66937,
    band3560: 119511,
    band6180: 109403,
    above80: 49407,
    absentees: 4011,
  },
  {
    subject: 'Mathematics',
    averageMark: 58.1,
    passPct: '79.2%',
    below35: 35974,
    band3560: 60216,
    band6180: 53464,
    above80: 23620,
    absentees: 1963,
  },
];

const HS_SUBJECTS = HS_SUBJECT_DETAIL.map((r) => r.subject);

const BAND_11_12: AcademicData = {
  scope:
    'Government, Partially Aided & Fully Aided schools only. Class 10 & 12 = board exam (Annual only); classes 6-10 at 5 core subjects, 11-12 are stream-based.',
  subjectCountLabel: 'Class 11-12 (streams)',
  profileBadge: 'Higher Secondary (Class 11 & 12) · 15 subjects',
  markBandTitle: 'Mark Band Distribution — Higher Secondary (Class 11 & 12)',
  subjectDetailTitle: 'Subject Detail — Higher Secondary (Class 11 & 12)',
  kpis: KPIS,
  metrics: {
    studentsExpected: 5253435,
    marksEntered: 4537990,
    strongestSubject: { name: 'History', value: 70.1 },
    weakestSubject: { name: 'Mathematics', value: 58.1 },
    below35Pct: 15.8,
  },
  subjectProfile: {
    labels: HS_SUBJECTS,
    state: HS_SUBJECT_DETAIL.map((r) => r.averageMark),
    selected: HS_SUBJECT_DETAIL.map((r) =>
      Number((r.averageMark - 1.6).toFixed(1))
    ),
  },
  markBand: {
    subjects: HS_SUBJECTS,
    below35: HS_SUBJECT_DETAIL.map((r) => toLakh(r.below35)),
    band35_60: HS_SUBJECT_DETAIL.map((r) => toLakh(r.band3560)),
    band61_80: HS_SUBJECT_DETAIL.map((r) => toLakh(r.band6180)),
    above80: HS_SUBJECT_DETAIL.map((r) => toLakh(r.above80)),
    // 0–100 values, read straight from the subject detail (not lakh counts).
    passPct: HS_SUBJECT_DETAIL.map((r) => parseFloat(r.passPct) || 0),
    avgMark: HS_SUBJECT_DETAIL.map((r) => r.averageMark),
  },
  examProgression: EXAM_PROGRESSION,
  classWise: CLASS_WISE,
  streamPerf: {
    streams: ['Arts', 'Commerce', 'Science (Biology)', 'Science (Maths)'],
    avgMark: [69.4, 66.6, 65.9, 64.6],
    passPct: [88, 86, 85, 84],
  },
  subjectDetail: HS_SUBJECT_DETAIL,
  originalSubjectDetail: HS_SUBJECT_DETAIL,
};

// ─────────────────────────────────────────────
// Accessors
// ─────────────────────────────────────────────

export const getAcademicData = (band: ClassBand): AcademicData =>
  band === '11-12' ? BAND_11_12 : BAND_6_10;

export const districtAcademicSummary: DistrictSummaryRow[] = DISTRICT_SUMMARY;

// ═════════════════════════════════════════════
// Trend Analysis tab
// ═════════════════════════════════════════════

export interface TrendData {
  years: string[];
  averageMark: number[];
  passPct: number[];
  compliance: number[];
}

export interface DistrictTrend {
  name: string;
  color: string;
  /** One value per year in TREND.years. */
  values: number[];
}

export interface DistrictChangeRow {
  name: string;
  from: number;
  to: number;
  change: number;
  [key: string]: unknown;
}

export const TREND: TrendData = {
  years: ['2024-25', '2025-26', '2026-27'],
  averageMark: [64.2, 65, 65.8],
  passPct: [83, 83.6, 84.4],
  compliance: [85, 85.7, 86.4],
};

const TREND_PALETTE = [
  '#e4842c',
  '#e4572e',
  '#f2c14e',
  '#7b1e3a',
  '#17a2b8',
  '#5cb85c',
  '#6f42c1',
  '#e8a33d',
  '#8b5e3c',
  '#2f5fb0',
  '#7cb342',
  '#8b1e3f',
];

// Most-improved districts (12 shown of 38) with a rising 3-year line.
export const DISTRICT_TRENDS: DistrictTrend[] = [
  { name: 'Kancheepuram', from: 54.8, to: 60.2 },
  { name: 'Sivagangai', from: 71.8, to: 77.2 },
  { name: 'Salem', from: 63.3, to: 68.6 },
  { name: 'Virudhunagar', from: 62, to: 67 },
  { name: 'Krishnagiri', from: 71.7, to: 76.3 },
  { name: 'Vellore', from: 73.3, to: 77.8 },
  { name: 'Tirunelveli', from: 51.2, to: 55.7 },
  { name: 'Thanjavur', from: 49.5, to: 53.9 },
  { name: 'Cuddalore', from: 65.4, to: 69.6 },
  { name: 'Tiruvallur', from: 59.9, to: 63.2 },
  { name: 'Madurai', from: 67, to: 70.2 },
  { name: 'Tirupathur', from: 57.5, to: 60.6 },
].map((d, i) => {
  const mid = Number(((d.from + d.to) / 2).toFixed(1));
  return {
    name: d.name,
    color: TREND_PALETTE[i % TREND_PALETTE.length],
    values: [d.from, mid, d.to],
  };
});

export const DISTRICT_CHANGE: DistrictChangeRow[] = DISTRICT_TRENDS.map(
  (d) => ({
    name: d.name,
    from: d.values[0],
    to: d.values[d.values.length - 1],
    change: Number((d.values[d.values.length - 1] - d.values[0]).toFixed(1)),
  })
);

// ═════════════════════════════════════════════
// Comparison tab — Subject vs District heatmap
// ═════════════════════════════════════════════

export interface HeatmapMatrix {
  subjects: string[];
  rows: { label: string; values: number[] }[];
}

// Districts × core subjects average marks (lower = redder).
export const COMPARISON_HEATMAP: HeatmapMatrix = {
  subjects: ['Language', 'English', 'Maths', 'Science', 'Social'],
  rows: [
    { label: 'Perambalur', values: [58.8, 57.7, 44.5, 48, 52.2] },
    { label: 'Thanjavur', values: [58.9, 53.8, 46.4, 49.9, 54.6] },
    { label: 'Tirunelveli', values: [60.7, 55.7, 48.1, 51.8, 56.1] },
    { label: 'Tiruppur', values: [60.9, 55.7, 48.4, 51.9, 56.2] },
    { label: 'The Nilgiris', values: [63.4, 58.3, 51, 54.5, 55.8] },
    { label: 'Karur', values: [63.3, 58.2, 50.9, 54.3, 58.7] },
    { label: 'Kallakurichi', values: [63.7, 58.7, 51.2, 54.8, 59.1] },
    { label: 'Mayiladuthurai', values: [64.9, 59, 51.4, 54.9, 59.5] },
    { label: 'Kancheepuram', values: [63.4, 58, 52.6, 56.3, 60.9] },
    { label: 'Tirupathur', values: [65.8, 60.7, 55.2, 58.8, 61.2] },
    { label: 'Ramanathapuram', values: [67.1, 62, 54.5, 57.9, 63.6] },
    { label: 'Tiruvarur', values: [67.4, 62.1, 54.7, 58.3, 62.7] },
    { label: 'Coimbatore', values: [67.4, 62.5, 55, 59.2, 62.9] },
    { label: 'Pudukkottai', values: [67.8, 62.8, 55.3, 58.8, 63.4] },
    { label: 'Erode', values: [68.2, 63.3, 55.8, 59.2, 63.7] },
    { label: 'Tiruvallur', values: [68.1, 63, 55.6, 59.1, 63.7] },
    { label: 'Villupuram', values: [68.5, 63.6, 55.9, 59.5, 64.1] },
    { label: 'Kanniyakumari', values: [70.4, 65.7, 57.8, 61.2, 66] },
    { label: 'Ariyalur', values: [70.5, 65.4, 57.9, 61.3, 66] },
    { label: 'Nagapattinam', values: [70.4, 65.6, 58, 61.4, 66] },
    { label: 'Chennai', values: [71.2, 66, 58.7, 62.3, 66.7] },
    { label: 'Dindigul', values: [71.4, 66.5, 58.9, 62.5, 67] },
    { label: 'Chengalpattu', values: [71.9, 66.8, 59.3, 62.9, 67.4] },
    { label: 'Salem', values: [73.6, 68.6, 61.1, 64.5, 69] },
    { label: 'Tiruchirappalli', values: [74.2, 69.2, 61.6, 65.3, 69.7] },
    { label: 'Madurai', values: [75.3, 70.3, 62.7, 66.7, 70.7] },
    { label: 'Krishnagiri', values: [81.4, 76.4, 68.6, 72.4, 76.7] },
    { label: 'Vellore', values: [82.8, 77.9, 70.4, 73.9, 78.5] },
  ],
};

// ═════════════════════════════════════════════
// Alarming tab
// ═════════════════════════════════════════════

export interface AlarmingKpis {
  highSeverity: number;
  mediumSeverity: number;
  totalAlerts: number;
  districtsFlagged: number;
}

export interface AlertRow {
  severity: string;
  district: string;
  alert: string;
  measured: string;
  [key: string]: unknown;
}

export interface RiskPoint {
  name: string;
  compliance: number;
  passPct: number;
  /** Quadrant group used to color the point. */
  group: 'critical' | 'under-reporting' | 'under-performing' | 'healthy';
}

export const ALARMING_KPIS: AlarmingKpis = {
  highSeverity: 0,
  mediumSeverity: 14,
  totalAlerts: 14,
  districtsFlagged: 14,
};

export const ALERTS_BY_CATEGORY = {
  categories: ['Low Data Compliance'],
  counts: [14],
};

export const ALERT_DETAILS: AlertRow[] = [
  {
    severity: 'Medium',
    district: 'Cuddalore',
    alert: 'Low Data Compliance',
    measured: '76.3% of 1,84,574 students entered',
  },
  {
    severity: 'Medium',
    district: 'Villupuram',
    alert: 'Low Data Compliance',
    measured: '76.7% of 1,57,130 students entered',
  },
  {
    severity: 'Medium',
    district: 'Perambalur',
    alert: 'Low Data Compliance',
    measured: '76.9% of 47,348 students entered',
  },
  {
    severity: 'Medium',
    district: 'Ramanathapuram',
    alert: 'Low Data Compliance',
    measured: '77.7% of 1,08,090 students entered',
  },
  {
    severity: 'Medium',
    district: 'Salem',
    alert: 'Low Data Compliance',
    measured: '78.1% of 2,63,714 students entered',
  },
  {
    severity: 'Medium',
    district: 'Thanjavur',
    alert: 'Low Data Compliance',
    measured: '78.2% of 1,88,509 students entered',
  },
  {
    severity: 'Medium',
    district: 'Sivagangai',
    alert: 'Low Data Compliance',
    measured: '80.9% of 1,15,812 students entered',
  },
  {
    severity: 'Medium',
    district: 'Madurai',
    alert: 'Low Data Compliance',
    measured: '81.2% of 2,33,890 students entered',
  },
  {
    severity: 'Medium',
    district: 'The Nilgiris',
    alert: 'Low Data Compliance',
    measured: '82% of 34,459 students entered',
  },
  {
    severity: 'Medium',
    district: 'Dindigul',
    alert: 'Low Data Compliance',
    measured: '82.1% of 1,81,378 students entered',
  },
  {
    severity: 'Medium',
    district: 'Vellore',
    alert: 'Low Data Compliance',
    measured: '82.3% of 1,18,447 students entered',
  },
  {
    severity: 'Medium',
    district: 'Ranipet',
    alert: 'Low Data Compliance',
    measured: '82.4% of 95,099 students entered',
  },
  {
    severity: 'Medium',
    district: 'Tirupathur',
    alert: 'Low Data Compliance',
    measured: '83.1% of 1,00,443 students entered',
  },
  {
    severity: 'Medium',
    district: 'Tiruvallur',
    alert: 'Low Data Compliance',
    measured: '83.8% of 1,56,742 students entered',
  },
];

// Risk quadrant scatter — data compliance (x) vs pass % (y).
export const RISK_POINTS: RiskPoint[] = [
  {
    name: 'Cuddalore',
    compliance: 76.3,
    passPct: 82,
    group: 'under-reporting',
  },
  {
    name: 'Villupuram',
    compliance: 76.7,
    passPct: 82.8,
    group: 'under-reporting',
  },
  { name: 'Perambalur', compliance: 76.9, passPct: 74, group: 'critical' },
  {
    name: 'Ramanathapuram',
    compliance: 77.7,
    passPct: 80,
    group: 'under-reporting',
  },
  { name: 'Thanjavur', compliance: 78.2, passPct: 73, group: 'critical' },
  { name: 'Salem', compliance: 78.1, passPct: 86.6, group: 'under-reporting' },
  {
    name: 'Sivagangai',
    compliance: 80.9,
    passPct: 90,
    group: 'under-reporting',
  },
  { name: 'Madurai', compliance: 81.2, passPct: 87, group: 'under-reporting' },
  { name: 'Dindigul', compliance: 82.1, passPct: 84, group: 'under-reporting' },
  {
    name: 'Vellore',
    compliance: 82.3,
    passPct: 93.3,
    group: 'under-reporting',
  },
  { name: 'Tiruvallur', compliance: 83.8, passPct: 82.5, group: 'healthy' },
  { name: 'Chennai', compliance: 88.8, passPct: 84.9, group: 'healthy' },
  { name: 'Chengalpattu', compliance: 87.1, passPct: 85.4, group: 'healthy' },
  { name: 'Krishnagiri', compliance: 92, passPct: 92, group: 'healthy' },
  {
    name: 'Kancheepuram',
    compliance: 92.4,
    passPct: 80.4,
    group: 'under-performing',
  },
  { name: 'Tiruppur', compliance: 93, passPct: 79, group: 'under-performing' },
  {
    name: 'Kallakurichi',
    compliance: 90.4,
    passPct: 79.1,
    group: 'under-performing',
  },
  { name: 'Tiruvannamalai', compliance: 94.2, passPct: 87.7, group: 'healthy' },
];

// ═════════════════════════════════════════════
// Caution tab
// ═════════════════════════════════════════════

export interface CautionCheck {
  key: string;
  label: string;
  count: number;
  severity: 'High' | 'Medium';
  meaning: string;
  icon: string;
  accent: string;
  [key: string]: unknown;
}

export interface CautionSchoolRow {
  district: string;
  block: string;
  school: string;
  udise: string;
  management: string;
  students: number;
  finding: string;
  [key: string]: unknown;
}

export const CAUTION_CHECKS: CautionCheck[] = [
  {
    key: 'under25',
    label: 'Mark Entry Under 25%',
    count: 92,
    severity: 'High',
    meaning:
      'Barely any marks uploaded, so the school average is not meaningful',
    icon: 'pi pi-flag',
    accent: '#e4572e',
  },
  {
    key: 'zeroCompliance',
    label: 'Zero Mark Entry Compliance',
    count: 81,
    severity: 'High',
    meaning:
      'No marks uploaded at all for 2026-27 — excluded from every average',
    icon: 'pi pi-flag-fill',
    accent: '#c0392b',
  },
  {
    key: 'zeroAvg',
    label: 'Zero Average Mark',
    count: 81,
    severity: 'High',
    meaning: 'No mark recorded against any subject',
    icon: 'pi pi-times-circle',
    accent: '#e67e22',
  },
  {
    key: 'under50',
    label: 'Mark Entry Under 50%',
    count: 92,
    severity: 'Medium',
    meaning: 'Less than half the assessments uploaded',
    icon: 'pi pi-exclamation-triangle',
    accent: '#b8860b',
  },
];

export const CAUTION_TOTAL_SCHOOLS = 173;

export const CAUTION_SCHOOLS: CautionSchoolRow[] = [
  {
    district: 'Dindigul',
    block: 'Dindigul -Urban',
    school: "ST. JOSEPH'S GIRLS HSS, WARD13, DINDIGUL",
    udise: '33131001105',
    management: 'Fully Aided School',
    students: 5126,
    finding: '8.4% entered (432 of 5,126)',
  },
  {
    district: 'Vellore',
    block: 'Pernambut',
    school: 'GGHSS PERNAMBUT',
    udise: '33041001013',
    management: 'School Education Department School',
    students: 1936,
    finding: '7.7% entered (150 of 1,936)',
  },
  {
    district: 'Villupuram',
    block: 'Thiruvennainallur',
    school: 'GOVERNMENT HIGHER SECONDARY SCHOOL MANAKKUPPAM',
    udise: '33073404302',
    management: 'School Education Department School',
    students: 1613,
    finding: '7.7% entered (125 of 1,613)',
  },
  {
    district: 'Cuddalore',
    block: 'Kattumannarkoil',
    school: 'GHSS(GIRLS), KATTUMANNARKOIL',
    udise: '33181403428',
    management: 'School Education Department School',
    students: 1553,
    finding: '7.5% entered (117 of 1,553)',
  },
  {
    district: 'Sivagangai',
    block: 'Devakottai',
    school: 'PETHAL ACHI GIRLS HR SEC SCHOOL, DEVAKOTTAI',
    udise: '33230603502',
    management: 'Partly Aided School',
    students: 1463,
    finding: '7.7% entered (113 of 1,463)',
  },
  {
    district: 'Tirunelveli',
    block: 'Palay -Rural',
    school: 'SANKAR HR SEC SCHOOL SANKARNAGAR 627357',
    udise: '33251206906',
    management: 'Fully Aided School',
    students: 1444,
    finding: '8.5% entered (123 of 1,444)',
  },
  {
    district: 'Thoothukudi',
    block: 'Thoothukudi Urban',
    school: 'ST MARYS BOYS HSS, MILLERPURAM',
    udise: '33280104202',
    management: 'Fully Aided School',
    students: 1354,
    finding: '7.4% entered (100 of 1,354)',
  },
  {
    district: 'Tiruchirappalli',
    block: 'Thuraiyur',
    school: 'GOVERNMENT GIRLS HIGHER SECONDARY SCHOOL, THURAIYUR',
    udise: '33111303505',
    management: 'School Education Department School',
    students: 1210,
    finding: '8.1% entered (98 of 1,210)',
  },
  {
    district: 'Ramanathapuram',
    block: 'Thiruppullani',
    school: "ST. JOSEPH'S HIGHER SECONDARY SCHOOL, MUTHUPETTAI",
    udise: '33271806702',
    management: 'Partly Aided School',
    students: 1149,
    finding: '7.7% entered (88 of 1,149)',
  },
  {
    district: 'Cuddalore',
    block: 'Annagrammam',
    school: 'GGHSS, NELLIKUPPAM',
    udise: '33181204516',
    management: 'School Education Department School',
    students: 1106,
    finding: '9% entered (100 of 1,106)',
  },
  {
    district: 'Salem',
    block: 'Valappadi',
    school: 'GGHSS, BELUR',
    udise: '33081102405',
    management: 'School Education Department School',
    students: 1056,
    finding: '6.2% entered (65 of 1,056)',
  },
  {
    district: 'Thanjavur',
    block: 'Thiruvonam',
    school: 'GHSS-THIRUVONAM',
    udise: '33210801306',
    management: 'School Education Department School',
    students: 921,
    finding: '8.9% entered (82 of 921)',
  },
];
